import { TFormField } from '@nirmata/shared';
import { toast } from '@nirmata/web-components';
import { CheckboxField } from '@nirmata/web-components/basic/checkbox-field';
import { Button } from '@nirmata/web-components/ui/button';
import { Input } from '@nirmata/web-components/ui/input';
import { Label } from '@nirmata/web-components/ui/label';
import { Plus, Save, Trash } from 'lucide-react';
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';

import { FORM_FIELDS_CONFIG } from '../../fields/fields.config';

interface BasicPropertiesProps {
  field: TFormField;
  onChange: (key: string, value: string | Record<string, unknown>) => void;
}

// Helper to find group and config for a given componentType
function getFieldGroupAndConfig(componentType: string) {
  for (const [group, configs] of Object.entries(FORM_FIELDS_CONFIG)) {
    for (const config of configs) {
      if (config.defaultProps.componentType === componentType) {
        return { group, config };
      }
    }
  }
  return { group: undefined, config: undefined };
}

export function BasicProperties({ field, onChange }: BasicPropertiesProps) {
  const [formState, setFormState] = useState({
    fieldName: field.fieldName,
    label: (field.componentProps?.label as string) || '',
    placeholder: (field.componentProps?.placeholder as string) || '',
    description: (field.componentProps?.description as string) || '',
    readOnly: (field.componentProps?.readOnly as boolean) || false,
    options: Array.isArray(field.componentProps?.options)
      ? (field.componentProps.options as Array<{ label: string; value: string }>)
      : [],
  });

  // Sync form state with field props when they change
  useEffect(() => {
    setFormState({
      fieldName: field.fieldName,
      label: (field.componentProps?.label as string) || '',
      placeholder: (field.componentProps?.placeholder as string) || '',
      description: (field.componentProps?.description as string) || '',
      readOnly: (field.componentProps?.readOnly as boolean | undefined) || false,
      options: Array.isArray(field.componentProps?.options)
        ? (field.componentProps.options as Array<{ label: string; value: string }>)
        : [],
    });
  }, [field]);

  const handleChange = (key: string) => (e: ChangeEvent<HTMLInputElement>) => {
    setFormState((prev) => ({
      ...prev,
      [key]: e.target.value,
    }));
  };

  // Add option state for new option
  const [newOption, setNewOption] = useState<{ label: string; value: string }>({
    label: '',
    value: '',
  });
  const [showAddOption, setShowAddOption] = useState(false);

  const handleOptionChange = (idx: number, key: 'label' | 'value', value: string) => {
    setFormState((prev) => {
      const options = prev.options.map((opt, i) => (i === idx ? { ...opt, [key]: value } : opt));
      return { ...prev, options };
    });
  };

  const handleDeleteOption = (idx: number) => {
    setFormState((prev) => {
      const options = prev.options.filter((_, i) => i !== idx);
      return { ...prev, options };
    });
  };

  const handleAddOption = () => {
    if (!newOption.label || !newOption.value) return;
    setFormState((prev) => ({
      ...prev,
      options: [...prev.options, { ...newOption }],
    }));
    setNewOption({ label: '', value: '' });
    setShowAddOption(false);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    // Update field name
    if (formState.fieldName !== field.fieldName) {
      onChange('fieldName', formState.fieldName);
    }

    // Update component props
    const componentProps = {
      ...field.componentProps,
      label: formState.label,
      placeholder: formState.placeholder,
      description: formState.description,
      readOnly: formState.readOnly,
      ...(group === 'Choice Fields' && { options: formState.options }),
    };
    onChange('componentProps', componentProps);

    toast.success('Basic properties saved successfully!');
  };

  // Determine group and config
  const { group, config } = getFieldGroupAndConfig(field.componentType);

  return (
    <form onSubmit={handleSubmit} className="grid gap-4">
      {/* Field Name - always present */}
      <div className="grid gap-2">
        <Label htmlFor="fieldName">Field Name</Label>
        <Input
          id="fieldName"
          name="fieldName"
          value={formState.fieldName}
          onChange={handleChange('fieldName')}
          placeholder="Enter field name"
        />
      </div>

      {/* Label - present for all */}
      <div className="grid gap-2">
        <Label htmlFor="label">Label</Label>
        <Input
          id="label"
          name="label"
          value={formState.label}
          onChange={handleChange('label')}
          placeholder="Enter label"
        />
      </div>

      {/* Description - only for Layout and Basic Fields */}
      {(group === 'Layout Components' || group === 'Basic Fields') && (
        <div className="grid gap-2">
          <Label htmlFor="description">Description</Label>
          <Input
            id="description"
            name="description"
            value={formState.description}
            onChange={handleChange('description')}
            placeholder="Enter field description"
          />
        </div>
      )}

      {/* Placeholder - only for Basic Fields */}
      {group === 'Basic Fields' && (
        <div className="grid gap-2">
          <Label htmlFor="placeholder">Placeholder</Label>
          <Input
            id="placeholder"
            name="placeholder"
            value={formState.placeholder}
            onChange={handleChange('placeholder')}
            placeholder="Enter placeholder text"
          />
        </div>
      )}

      <div className="grid gap-2">
        <CheckboxField
          id="readOnly"
          name="readOnly"
          label="Read Only"
          checked={formState.readOnly}
          onChange={(e) => {
            e.preventDefault();
            setFormState((prev) => ({
              ...prev,
              readOnly: e.target.checked,
            }));
          }}
        />
      </div>

      {/* Options - only for Choice Fields */}
      {group === 'Choice Fields' && (
        <div className="grid gap-2">
          <Label>Options</Label>
          {formState.options.map((opt, idx) => (
            <div key={idx} className="flex gap-2 items-center">
              <Input
                value={opt.label}
                onChange={(e) => handleOptionChange(idx, 'label', e.target.value)}
                placeholder="Label"
                className="flex-1"
              />
              <Input
                value={opt.value}
                onChange={(e) => handleOptionChange(idx, 'value', e.target.value)}
                placeholder="Value"
                className="flex-1"
              />
              <button
                type="button"
                className="p-1 rounded hover:bg-[var(--destructive)]/10"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteOption(idx);
                }}
                title="Delete option"
                aria-label="Delete option"
              >
                <Trash className="w-4 h-4 text-[var(--destructive)] cursor-pointer" />
              </button>
            </div>
          ))}
          {showAddOption ? (
            <div className="flex gap-2 items-center mt-2">
              <Input
                value={newOption.label}
                onChange={(e) => setNewOption((opt) => ({ ...opt, label: e.target.value }))}
                placeholder="Label"
                className="flex-1"
              />
              <Input
                value={newOption.value}
                onChange={(e) => setNewOption((opt) => ({ ...opt, value: e.target.value }))}
                placeholder="Value"
                className="flex-1"
              />
              <button
                type="button"
                className="p-1 rounded hover:bg-[var(--primary)]/10"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddOption();
                }}
                title="Add option"
                aria-label="Add option"
              >
                <Plus className="w-4 h-4 text-[var(--primary)] cursor-pointer" />
              </button>
            </div>
          ) : (
            <Button
              type="button"
              className="cursor-pointer w-1/6"
              onClick={(e) => {
                e.stopPropagation();
                setShowAddOption(true);
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Option
            </Button>
          )}
        </div>
      )}

      {/* Icon - only for Layout Components */}
      {group === 'Layout Components' && config && (
        <div className="flex items-center gap-2">
          <Label>Icon</Label>
          <config.icon className="h-5 w-5" />
          <span className="text-sm text-[var(--muted-foreground)]">({config.label})</span>
        </div>
      )}

      <div className="flex justify-end mt-4">
        <Button type="button" className="cursor-pointer" onClick={handleSubmit}>
          <Save className="h-4 w-4 mr-2" />
          Save Basic Properties
        </Button>
      </div>
    </form>
  );
}
